import { ValidationResult, ValidationError, ValidationWarning, ValidationRule } from '../types';

// Main validation function
export const validateData = (
  data: any[],
  rules: ValidationRule[],
  requiredFields: string[] = [],
  uniqueFields: string[] = []
): ValidationResult => {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  // Validate required fields
  const requiredErrors = validateRequiredFields(data, requiredFields);
  errors.push(...requiredErrors);

  // Validate unique fields
  const uniqueErrors = validateUniqueFields(data, uniqueFields);
  errors.push(...uniqueErrors);

  // Apply custom validation rules
  data.forEach((row, index) => {
    rules.forEach(rule => {
      const fieldValue = row[rule.field];
      
      if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
        let isValid = true;
        
        switch (rule.rule) {
          case 'email':
            isValid = validateEmail(String(fieldValue));
            break;
          case 'phone':
            isValid = validatePhone(String(fieldValue));
            break;
          case 'date':
            isValid = validateDate(String(fieldValue));
            break;
          case 'number':
            isValid = validateNumber(fieldValue);
            break;
          case 'custom':
            isValid = rule.customValidator ? rule.customValidator(fieldValue) : true;
            break;
          default:
            isValid = true;
        }
        
        if (!isValid) {
          errors.push({
            field: rule.field,
            message: rule.message,
            row: index + 1
          });
        }
      }
    });
  });

  // Check for data quality warnings
  const qualityWarnings = generateQualityWarnings(data);
  warnings.push(...qualityWarnings);

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

// Required field validation
export const validateRequiredFields = (data: any[], requiredFields: string[]): ValidationError[] => {
  const errors: ValidationError[] = [];
  
  data.forEach((row, index) => {
    requiredFields.forEach(field => {
      const value = row[field];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors.push({
          field,
          message: `${field} is required`,
          row: index + 1
        });
      }
    });
  });
  
  return errors;
};

// Unique field validation
export const validateUniqueFields = (data: any[], uniqueFields: string[]): ValidationError[] => {
  const errors: ValidationError[] = [];
  const seen = new Map<string, Set<string>>();
  
  uniqueFields.forEach(field => {
    seen.set(field, new Set());
  });
  
  data.forEach((row, index) => {
    uniqueFields.forEach(field => {
      const value = row[field];
      if (value !== undefined && value !== null && value !== '') {
        const fieldSeen = seen.get(field);
        if (fieldSeen && fieldSeen.has(String(value))) {
          errors.push({
            field,
            message: `${field} must be unique. Duplicate value: ${value}`,
            row: index + 1
          });
        } else if (fieldSeen) {
          fieldSeen.add(String(value));
        }
      }
    });
  });
  
  return errors;
};

// Email validation
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Phone validation
export const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
};

// Date validation
export const validateDate = (date: string): boolean => {
  const dateObj = new Date(date);
  return dateObj instanceof Date && !isNaN(dateObj.getTime());
};

// Number validation
export const validateNumber = (value: any, min?: number, max?: number): boolean => {
  const num = Number(value);
  if (isNaN(num)) return false;
  if (min !== undefined && num < min) return false;
  if (max !== undefined && num > max) return false;
  return true;
};

// URL validation
export const validateURL = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// JSON validation
export const validateJSON = (jsonString: string): boolean => {
  try {
    JSON.parse(jsonString);
    return true;
  } catch {
    return false;
  }
};

// Generate quality warnings
export const generateQualityWarnings = (data: any[]): ValidationWarning[] => {
  const warnings: ValidationWarning[] = [];
  
  if (data.length === 0) {
    warnings.push({
      field: 'data',
      message: 'No data to validate'
    });
    return warnings;
  }
  
  // Check for empty rows
  const emptyRows = data.filter(row => 
    Object.values(row).every(value => 
      value === null || value === undefined || value === ''
    )
  );
  
  if (emptyRows.length > 0) {
    warnings.push({
      field: 'data',
      message: `${emptyRows.length} empty rows found`
    });
  }
  
  // Check for incomplete rows
  const totalFields = Object.keys(data[0]).length;
  const incompleteRows = data.filter(row => 
    Object.values(row).filter(value => 
      value !== null && value !== undefined && value !== ''
    ).length < totalFields * 0.5
  );
  
  if (incompleteRows.length > 0) {
    warnings.push({
      field: 'data',
      message: `${incompleteRows.length} rows appear to be incomplete`
    });
  }
  
  // Check for potential duplicates
  const potentialDuplicates = findPotentialDuplicates(data);
  if (potentialDuplicates.length > 0) {
    warnings.push({
      field: 'data',
      message: `${potentialDuplicates.length} potential duplicate rows found`
    });
  }
  
  return warnings;
};

// Find potential duplicates
export const findPotentialDuplicates = (data: any[]): number[] => {
  const duplicates: number[] = [];
  const seen = new Map<string, number>();
  
  data.forEach((row, index) => {
    // Create a hash of the row data
    const rowHash = JSON.stringify(row);
    
    if (seen.has(rowHash)) {
      duplicates.push(index);
      if (seen.get(rowHash) !== undefined) {
        duplicates.push(seen.get(rowHash)!);
      }
    } else {
      seen.set(rowHash, index);
    }
  });
  
  return Array.from(new Set(duplicates));
};

// Validate file format
export const validateFileFormat = (file: File, allowedFormats: string[]): boolean => {
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  return fileExtension ? allowedFormats.includes(fileExtension) : false;
};

// Validate file size
export const validateFileSize = (file: File, maxSize: string): boolean => {
  const maxSizeBytes = parseFileSize(maxSize);
  return file.size <= maxSizeBytes;
};

// Browser memory management - limit file size to ~1MB for optimal performance
export const validateFileSizeForMemory = (file: File): { isValid: boolean; error?: string } => {
  const MAX_FILE_SIZE = 1024 * 1024; // 1MB in bytes
  const MAX_COMPRESSED_SIZE = 512 * 1024; // 512KB for compressed files
  
  // Check if it's a compressed file (ZIP, RAR, etc.)
  const isCompressed = file.name.toLowerCase().match(/\.(zip|rar|7z|gz|bz2)$/);
  const maxAllowedSize = isCompressed ? MAX_COMPRESSED_SIZE : MAX_FILE_SIZE;
  
  if (file.size > maxAllowedSize) {
    const maxSizeMB = (maxAllowedSize / (1024 * 1024)).toFixed(1);
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      isValid: false,
      error: `File size (${fileSizeMB}MB) exceeds the maximum allowed size of ${maxSizeMB}MB for ${isCompressed ? 'compressed' : 'uncompressed'} files. This limit ensures optimal browser performance and memory management.`
    };
  }
  
  return { isValid: true };
};

// Estimate uncompressed size for ZIP files (rough estimation)
export const estimateUncompressedSize = (file: File): number => {
  if (!file.name.toLowerCase().endsWith('.zip')) {
    return file.size;
  }
  
  // Rough estimation: ZIP files typically compress at 2-5x ratio
  // We'll use a conservative 3x multiplier for safety
  return file.size * 3;
};

// Validate file size considering potential uncompressed size
export const validateFileSizeWithUncompressedEstimate = (file: File): { isValid: boolean; error?: string } => {
  const MAX_UNCOMPRESSED_SIZE = 1024 * 1024; // 1MB uncompressed limit
  
  if (file.name.toLowerCase().endsWith('.zip')) {
    const estimatedUncompressedSize = estimateUncompressedSize(file);
    
    if (estimatedUncompressedSize > MAX_UNCOMPRESSED_SIZE) {
      const estimatedSizeMB = (estimatedUncompressedSize / (1024 * 1024)).toFixed(1);
      return {
        isValid: false,
        error: `The ZIP file may contain uncompressed data larger than 1MB (estimated ${estimatedSizeMB}MB). This could cause browser memory issues. Please use a smaller file or extract and re-compress with higher compression.`
      };
    }
  }
  
  // Also check the compressed file size
  return validateFileSizeForMemory(file);
};

// Parse file size string (e.g., "10MB", "5KB", "1GB")
export const parseFileSize = (sizeString: string): number => {
  const units: Record<string, number> = {
    'B': 1,
    'KB': 1024,
    'MB': 1024 * 1024,
    'GB': 1024 * 1024 * 1024
  };
  
  const match = sizeString.match(/^(\d+(?:\.\d+)?)\s*([A-Z]+)$/i);
  if (!match) return 0;
  
  const size = parseFloat(match[1]);
  const unit = match[2].toUpperCase();
  
  return size * (units[unit] || 1);
};

// Validate data types
export const validateDataType = (value: any, expectedType: string): boolean => {
  switch (expectedType.toLowerCase()) {
    case 'string':
      return typeof value === 'string';
    case 'number':
      return typeof value === 'number' && !isNaN(value);
    case 'boolean':
      return typeof value === 'boolean';
    case 'date':
      return value instanceof Date || (typeof value === 'string' && validateDate(value));
    case 'array':
      return Array.isArray(value);
    case 'object':
      return typeof value === 'object' && value !== null && !Array.isArray(value);
    default:
      return true;
  }
};

// Sanitize data
export const sanitizeData = (data: any[]): any[] => {
  return data.map(row => {
    const sanitized: any = {};
    
    Object.keys(row).forEach(key => {
      const value = row[key];
      
      if (typeof value === 'string') {
        // Remove potentially dangerous characters
        sanitized[key] = value
          .replace(/[<>]/g, '') // Remove < and >
          .replace(/javascript:/gi, '') // Remove javascript: protocol
          .replace(/on\w+=/gi, '') // Remove event handlers
          .trim();
      } else {
        sanitized[key] = value;
      }
    });
    
    return sanitized;
  });
};

// Create validation summary
export const createValidationSummary = (result: ValidationResult): string => {
  const { errors, warnings } = result;
  
  let summary = `Validation Summary:\n`;
  summary += `✅ Valid: ${result.isValid ? 'Yes' : 'No'}\n`;
  summary += `❌ Errors: ${errors.length}\n`;
  summary += `⚠️ Warnings: ${warnings.length}\n`;
  
  if (errors.length > 0) {
    summary += `\nErrors:\n`;
    errors.forEach(error => {
      summary += `  • ${error.field}: ${error.message}${error.row ? ` (Row ${error.row})` : ''}\n`;
    });
  }
  
  if (warnings.length > 0) {
    summary += `\nWarnings:\n`;
    warnings.forEach(warning => {
      summary += `  • ${warning.field}: ${warning.message}${warning.row ? ` (Row ${warning.row})` : ''}\n`;
    });
  }
  
  return summary;
};
